import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import NewsletterSubscribe from '../../../article/components/NewsletterSubscribe/NewsletterSubscribe';
import { Image, ImageVariant } from '../../../common/components/Image';
import { getMatchStatusLabel } from '../../../utils/getMatchStatusLabel';
import { parseMatchStatus } from '../../../utils/parseMatchStatus';
import ArticleListUI from '../../../article/components/ArticleList/ArticleListUI';
import { ArticleVariant, PaginatedResponse, Article, LeagueTable, Match } from '@repo/types';
import FixtureListUI from '../FixtureList/FixtureListUI';
import LeagueTableUI from '../../../league/components/LeagueTable/LeagueTableUI';
import { LeagueTableColumn } from '../../../league/components/LeagueTable/leagueTableColumns';
import LinkButton from '../../../common/components/Link/Link';
import { LinkVariant, LinkSize } from '../../../common/components/Link/contracts';
import { Link } from 'react-router';
import { useMemo } from 'react';
import { SponsorsGrid } from '@ui/league/SponsorsGrid/SponsorsGrid';

export type MatchOverviewProps = {
  match: Match;
  isMobile: boolean;
  homeTeamArticles?: PaginatedResponse<Article>;
  awayTeamArticles?: PaginatedResponse<Article>;
  homeTeamResults?: PaginatedResponse<Match>;
  awayTeamResults?: PaginatedResponse<Match>;
  homeTeamFixtures?: PaginatedResponse<Match>;
  awayTeamFixtures?: PaginatedResponse<Match>;
  homeTeamLeagueTable?: LeagueTable;
  awayTeamLeagueTable?: LeagueTable;
};

// @ts-ignore fix vite import type
const isLeagueSite = import.meta.env.VITE_SITE_TYPE === 'league';

export function MatchOverview({
  match,
  isMobile,
  homeTeamArticles,
  awayTeamArticles,
  homeTeamResults,
  homeTeamFixtures,
  awayTeamResults,
  awayTeamFixtures,
  homeTeamLeagueTable,
  awayTeamLeagueTable,
}: MatchOverviewProps) {
  const { isPastMatch, isLiveMatch, isFutureMatch } = parseMatchStatus(match.status);

  const { homeTeamSlug, awayTeamSlug } = useMemo(() => {
    if (isLeagueSite) {
      return {
        homeTeamSlug: match.homeTeam?.club?.slug ? `/clubs/${match.homeTeam?.club?.slug}` : '',
        awayTeamSlug: match.awayTeam?.club?.slug ? `/clubs/${match.awayTeam?.club?.slug}` : '',
      };
    }

    return {
      homeTeamSlug: match.homeTeam?.slug ? `/teams/${match.homeTeam?.slug}` : '',
      awayTeamSlug: match.awayTeam?.slug ? `/teams/${match.awayTeam?.slug}` : '',
    };
  }, [match.homeTeam, match.awayTeam]);

  const matchCurrentMinute = useMemo(() => {
    if (isLiveMatch && match.currentMinute) {
      const [minute] = match.currentMinute.split(':');
      return minute;
    }
  }, [isLiveMatch, match.currentMinute]);

  return (
    <div className="match-overview">
      <div className="match-overview__content">
        <div className="match-overview__match-container">
          <div className="match-overview__match-header-container">
            <div className="match-overview__title-container">
              <h3 className="match-overview__title">{match.competition?.name ?? ''}</h3>
              <span className="match-overview__sub-title">
                {format(parseISO(match.date), 'EEEE dd LLLL yyyy')}
                {match.stadium ? `,at ${match.stadium}` : ''}
              </span>
              {match.attendance && match.attendance > 0 ? (
                <span className="match-overview__attendance">Attendance: {match.attendance}</span>
              ) : null}
              {match.info && <div dangerouslySetInnerHTML={{ __html: match.info }} />}
            </div>
          </div>
        </div>
        <div className="match-overview__clubs-container">
          <div className="match-overview__home-team-container">
            <Link className="match-overview__home-team" to={homeTeamSlug}>
              <h2 className="match-overview__home-team-name">{match.homeTeam?.fullName}</h2>
            </Link>
          </div>
          <div className="match-overview__score-container">
            <Link to={homeTeamSlug}>
              <Image
                src={match.homeTeam?.logo ?? 'default-team-badge.webp'}
                alt={`${match.homeTeam?.fullName} logo`}
                width={50}
                height={50}
                variant={ImageVariant.SquareLogoLarge}
              />
            </Link>
            <div
              className={cn('match-overview__score-wrapper', {
                'match-overview__score-wrapper--future': isFutureMatch,
                'match-overview__score-wrapper--live': isLiveMatch,
                'match-overview__score-wrapper--past': isPastMatch,
              })}
            >
              {match.score?.penalties && (
                <span>
                  {match.score.penalties.home} P {match.score.penalties.away}
                </span>
              )}
              {isFutureMatch && (
                <span className={cn('match-overview__score', 'match-overview__score--future')}>{match.time}</span>
              )}
              {isLiveMatch && (
                <span
                  className="match-overview__score"
                  style={{
                    marginBottom: match.score?.penalties ? 'auto' : undefined,
                  }}
                >
                  {match.score.current.home} - {match.score.current.away}
                </span>
              )}
              {isPastMatch && (
                <span
                  className={cn('match-overview__score', 'match-overview__score--past')}
                  style={{
                    marginBottom: match.score?.penalties ? 'auto' : undefined,
                  }}
                >
                  {match.score.current.home} - {match.score.current.away}
                </span>
              )}
            </div>
            <Link to={awayTeamSlug}>
              <Image
                src={match.awayTeam?.logo ?? 'default-team-badge.webp'}
                alt={`${match.awayTeam?.fullName} logo`}
                width={50}
                height={50}
                variant={ImageVariant.SquareLogoLarge}
              />
            </Link>
          </div>
          <div className="match-overview__away-team-container">
            <Link className="match-overview__away-team" to={awayTeamSlug}>
              <h2 className="match-overview__away-team-name">{match.awayTeam?.fullName}</h2>
            </Link>
          </div>
        </div>
        {!isFutureMatch && (
          <div className="match-overview__goalscorers-container">
            <ul className="match-overview__home-goalscorers-list">
              {match.goals?.home &&
                match.goals.home.map((goal) => (
                  <li>{`${goal.player.firstName} ${goal.player.lastName} (${goal.minute}')`}</li>
                ))}
            </ul>
            <ul className="match-overview__match-status-list">
              {isLiveMatch && <li>{matchCurrentMinute}'</li>}
              <li>{getMatchStatusLabel(match.status).long}</li>
              {match.score.halfTime ? (
                <li>
                  HT {match.score.halfTime.home} - {match.score.halfTime.away}
                </li>
              ) : null}
            </ul>
            <ul className="match-overview__away-goalscorers-list">
              {match.goals?.away &&
                match.goals.away.map((goal) => (
                  <li>
                    <li>{`${goal.player.firstName} ${goal.player.lastName} (${goal.minute}')`}</li>
                  </li>
                ))}
            </ul>
          </div>
        )}

        <div className="match-overview__match-details-container">
          {match.lineups &&
            match.lineups.home &&
            match.lineups.home.length > 0 &&
            match.lineups.away &&
            match.lineups.away.length > 0 && (
              <div className="match-overview__lineups-container">
                <div className="match-overview__lineups-lists-container">
                  <div
                    className="match-overview__lineups-list-wrapper match-overview__lineups-list-wrapper--home"
                    style={{ marginLeft: 'auto' }}
                  >
                    {isMobile && <h3 className="match-overview__lineups-home-team-name">{match.homeTeam?.fullName}</h3>}
                    <ul className="match-overview__lineups-list">
                      {match.lineups?.home.map((lineup, index) => (
                        <li className="match-overview__lineups-list-item">
                          <div>
                            <span className="match-overview__lineups-player-shirt-number">{lineup.shirtNumber}</span>
                            <span className="match-overview__lineups-player-name">
                              {lineup.player.firstName} {lineup.player.lastName} {index === 0 ? '(GK)' : ''}
                              {index === 0 && lineup.isCaptain ? ' ' : ''}
                              {lineup.isCaptain ? `(C)` : ''}
                            </span>
                          </div>
                          <div className="match-overview__lineups-player-info-container">
                            {lineup.goals?.map((goal) => (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                                <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                              </div>
                            ))}
                            {lineup.replacedBy && (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">
                                  {lineup.replacedBy.minute}'
                                </span>
                                <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                              </div>
                            )}
                            {lineup.yellowCards &&
                              lineup.yellowCards.map(() => <div className={'match-overview__yellow-card'} />)}
                            {lineup.redCard && <div className={'match-overview__red-card'} />}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <hr style={{ width: '100%' }} />
                    <ul className="match-overview__lineups-list">
                      {match.subs?.home?.map((sub) => (
                        <li className="match-overview__lineups-list-item">
                          <div>
                            <span className="match-overview__lineups-player-shirt-number">{sub.shirtNumber}</span>
                            <span className="match-overview__lineups-player-name">
                              {sub.player.firstName} {sub.player.lastName}
                            </span>
                          </div>
                          <div className="match-overview__lineups-player-info-container">
                            {sub.goals?.map((goal) => (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                                <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                              </div>
                            ))}
                            {sub.replaces && (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">
                                  {sub.replaces.minute}'
                                </span>
                                <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                              </div>
                            )}
                            {sub.yellowCards &&
                              sub.yellowCards.map(() => <div className={'match-overview__yellow-card'} />)}
                            {sub.redCard && <div className={'match-overview__red-card'} />}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="match-overview__lineups-list-wrapper">
                    {isMobile && <h3 className="match-overview__lineups-away-team-name">{match.awayTeam?.fullName}</h3>}
                    <ul className="match-overview__lineups-list">
                      {match.lineups?.away.map((lineup, index) => (
                        <li className="match-overview__lineups-list-item">
                          <div>
                            <span className="match-overview__lineups-player-shirt-number">{lineup.shirtNumber}</span>
                            <span className="match-overview__lineups-player-name">
                              {lineup.player.firstName} {lineup.player.lastName} {index === 0 ? '(GK)' : ''}
                              {index === 0 && lineup.isCaptain ? ' ' : ''}
                              {lineup.isCaptain ? `(C)` : ''}
                            </span>
                          </div>
                          <div className="match-overview__lineups-player-info-container">
                            {lineup.goals?.map((goal) => (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                                <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                              </div>
                            ))}
                            {lineup.replacedBy && (
                              <div className="match-overview__lineups-player-sub-container">
                                <span className="match-overview__lineups-player-sub-minute">
                                  {lineup.replacedBy.minute}'
                                </span>
                                <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                              </div>
                            )}
                            {lineup.yellowCards &&
                              lineup.yellowCards.map(() => <div className={'match-overview__yellow-card'} />)}
                            {lineup.redCard && <div className={'match-overview__red-card'} />}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <hr style={{ width: '100%' }} />
                    <ul className="match-overview__lineups-list">
                      {match.subs?.away?.map((sub) => (
                        <li className="match-overview__lineups-list-item">
                          <div>
                            <span className="match-overview__lineups-player-shirt-number">{sub?.shirtNumber}</span>
                            <span className="match-overview__lineups-player-name">
                              {sub?.player.firstName} {sub?.player.lastName}
                            </span>
                          </div>
                          <div className="match-overview__lineups-player-info-container">
                            <div className="match-overview__lineups-player-info-container">
                              {sub?.goals?.map((goal) => (
                                <div className="match-overview__lineups-player-sub-container">
                                  <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                                  <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                                </div>
                              ))}
                              {sub?.replaces && (
                                <div className="match-overview__lineups-player-sub-container">
                                  <span className="match-overview__lineups-player-sub-minute">
                                    {sub?.replaces.minute}'
                                  </span>
                                  <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                                </div>
                              )}
                              {sub?.yellowCards &&
                                sub?.yellowCards.map(() => <div className={'match-overview__yellow-card'} />)}
                              {sub?.redCard && <div className={'match-overview__red-card'} />}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
        </div>

        {isMobile ? (
          <>
            {homeTeamLeagueTable?.competition && (
              <div className="match-overview__news-container">
                <div className="match-overview__league-table-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <LeagueTableUI
                      leagueTable={homeTeamLeagueTable}
                      limit={3}
                      highlightedClubId={match.homeTeam.id}
                      columns={[
                        LeagueTableColumn.Rank,
                        LeagueTableColumn.Team,
                        LeagueTableColumn.GP,
                        LeagueTableColumn.W,
                        LeagueTableColumn.D,
                        LeagueTableColumn.L,
                        LeagueTableColumn.GD,
                        LeagueTableColumn.P,
                      ]}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">${homeTeamLeagueTable.competition.name}</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {homeTeamResults && Boolean(homeTeamResults?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <FixtureListUI
                      fixtures={homeTeamResults.items}
                      groupByDate={true}
                      columns={1}
                      linkLabel="VIEW ALL"
                      linkUrl={`/results?team=${match.homeTeam?.id}`}
                      linkSize={LinkSize.sm}
                      linkVariant={LinkVariant.Text}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Recent results</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {homeTeamFixtures && Boolean(homeTeamFixtures?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <FixtureListUI
                      fixtures={homeTeamFixtures.items}
                      groupByDate={true}
                      columns={1}
                      linkLabel="VIEW ALL"
                      linkUrl={`/results?team=${match.homeTeam?.id}`}
                      linkSize={LinkSize.sm}
                      linkVariant={LinkVariant.Text}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Upcoming fixtures</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {homeTeamArticles && Boolean(homeTeamArticles?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container">
                    <div>
                      <h2
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        Latest news
                      </h2>
                      <hr />
                    </div>
                    <ArticleListUI
                      paginatedArticles={homeTeamArticles}
                      variant={ArticleVariant.TextSideBySide}
                      showDate={false}
                      columns={isMobile ? 1 : awayTeamArticles && awayTeamArticles?.items?.length > 0 ? 1 : 3}
                    />
                    <LinkButton
                      label={`More ${match.homeTeam.shortName} news`}
                      url={
                        isLeagueSite ? `/news?tags=${match.homeTeam?.club?.slug}` : `/news?tags=${match.homeTeam?.slug}`
                      }
                      openInNewTab={false}
                      variant={LinkVariant.Text}
                      size={LinkSize.sm}
                    />
                  </div>
                </div>
              </div>
            )}

            {awayTeamLeagueTable?.competition && (
              <div className="match-overview__news-container">
                <div className="match-overview__league-table-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <LeagueTableUI
                      leagueTable={awayTeamLeagueTable}
                      limit={3}
                      highlightedClubId={match.awayTeam.id}
                      columns={[
                        LeagueTableColumn.Rank,
                        LeagueTableColumn.Team,
                        LeagueTableColumn.GP,
                        LeagueTableColumn.W,
                        LeagueTableColumn.D,
                        LeagueTableColumn.L,
                        LeagueTableColumn.GD,
                        LeagueTableColumn.P,
                      ]}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">${awayTeamLeagueTable.competition.name}</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {awayTeamResults && Boolean(awayTeamResults?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <FixtureListUI
                      fixtures={awayTeamResults.items}
                      groupByDate={true}
                      columns={1}
                      linkLabel="VIEW ALL"
                      linkUrl={`/results?team=${match.awayTeam?.id}`}
                      linkSize={LinkSize.sm}
                      linkVariant={LinkVariant.Text}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Recent results</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {awayTeamFixtures && Boolean(awayTeamFixtures?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    <FixtureListUI
                      fixtures={awayTeamFixtures.items}
                      groupByDate={true}
                      columns={1}
                      linkLabel="VIEW ALL"
                      linkUrl={`/results?team=${match.awayTeam?.id}`}
                      linkSize={LinkSize.sm}
                      linkVariant={LinkVariant.Text}
                      title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Upcoming fixtures</h2><hr/>`}
                    />
                  </div>
                </div>
              </div>
            )}

            {awayTeamArticles && Boolean(awayTeamArticles?.items?.length) && (
              <div className="match-overview__news-container">
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container">
                    <div>
                      <h2
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        Latest news
                      </h2>
                      <hr />
                    </div>
                    <ArticleListUI
                      paginatedArticles={awayTeamArticles}
                      variant={ArticleVariant.TextSideBySide}
                      showDate={false}
                      columns={isMobile ? 1 : awayTeamArticles && awayTeamArticles?.items?.length > 0 ? 1 : 3}
                    />
                    <LinkButton
                      label={`More ${match.awayTeam.shortName} news`}
                      url={
                        isLeagueSite ? `/news?tags=${match.awayTeam?.club?.slug}` : `/news?tags=${match.awayTeam?.slug}`
                      }
                      openInNewTab={false}
                      variant={LinkVariant.Text}
                      size={LinkSize.sm}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {(homeTeamLeagueTable?.competition || awayTeamLeagueTable?.competition) && (
              <div className="match-overview__news-container">
                <h3>League Tables</h3>
                <div className="match-overview__league-table-list-container">
                  {homeTeamLeagueTable && homeTeamLeagueTable.competition && (
                    <div className="match-overview__news-team-container match-overview__news-team-container--left">
                      <span className="match-overview__news-team-name">{homeTeamLeagueTable.competition.name}</span>
                      <LeagueTableUI
                        leagueTable={homeTeamLeagueTable}
                        highlightedClubId={match.homeTeam.id}
                        columns={[
                          LeagueTableColumn.Rank,
                          LeagueTableColumn.Team,
                          LeagueTableColumn.GP,
                          LeagueTableColumn.W,
                          LeagueTableColumn.D,
                          LeagueTableColumn.L,
                          LeagueTableColumn.GD,
                          LeagueTableColumn.P,
                        ]}
                      />
                    </div>
                  )}
                  {awayTeamLeagueTable && awayTeamLeagueTable.competition && (
                    <div className="match-overview__news-team-container match-overview__news-team-container--right">
                      <span className="match-overview__news-team-name">{awayTeamLeagueTable.competition.name}</span>
                      <LeagueTableUI
                        leagueTable={awayTeamLeagueTable}
                        highlightedClubId={match.awayTeam.id}
                        columns={[
                          LeagueTableColumn.Rank,
                          LeagueTableColumn.Team,
                          LeagueTableColumn.GP,
                          LeagueTableColumn.W,
                          LeagueTableColumn.D,
                          LeagueTableColumn.L,
                          LeagueTableColumn.GD,
                          LeagueTableColumn.P,
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {Boolean(homeTeamResults?.items?.length || awayTeamResults?.items?.length) && (
              <div className="match-overview__news-container">
                <h3>Recent results</h3>
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    {homeTeamResults && homeTeamResults.items.length > 0 ? (
                      <FixtureListUI
                        fixtures={homeTeamResults.items}
                        groupByDate={true}
                        columns={1}
                        linkLabel="VIEW ALL"
                        linkUrl={`/results?team=${match.homeTeam?.id}`}
                        linkSize={LinkSize.sm}
                        linkVariant={LinkVariant.Text}
                      />
                    ) : (
                      <span>No matches available</span>
                    )}
                  </div>
                  <div className="match-overview__news-team-container match-overview__news-team-container--right">
                    {awayTeamResults && awayTeamResults.items.length > 0 ? (
                      <FixtureListUI
                        fixtures={awayTeamResults.items}
                        groupByDate={true}
                        columns={1}
                        linkLabel="VIEW ALL"
                        linkUrl={`/results?team=${match.awayTeam?.id}`}
                        linkSize={LinkSize.sm}
                        linkVariant={LinkVariant.Text}
                      />
                    ) : (
                      <span>No matches available</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Boolean(homeTeamFixtures?.items?.length || awayTeamFixtures?.items?.length) && (
              <div className="match-overview__news-container">
                <h3>Upcoming fixtures</h3>
                <div className="match-overview__news-list-container">
                  <div className="match-overview__news-team-container match-overview__news-team-container--left">
                    {homeTeamFixtures && homeTeamFixtures.items.length > 0 ? (
                      <FixtureListUI
                        fixtures={homeTeamFixtures.items}
                        groupByDate={true}
                        columns={1}
                        linkLabel="VIEW ALL"
                        linkUrl={`/results?team=${match.homeTeam?.id}`}
                        linkSize={LinkSize.sm}
                        linkVariant={LinkVariant.Text}
                      />
                    ) : (
                      <span>No matches available</span>
                    )}
                  </div>
                  <div className="match-overview__news-team-container match-overview__news-team-container--right">
                    {awayTeamFixtures && awayTeamFixtures.items.length > 0 ? (
                      <FixtureListUI
                        fixtures={awayTeamFixtures.items}
                        groupByDate={true}
                        columns={1}
                        linkLabel="VIEW ALL"
                        linkUrl={`/results?team=${match.awayTeam?.id}`}
                        linkSize={LinkSize.sm}
                        linkVariant={LinkVariant.Text}
                      />
                    ) : (
                      <span>No matches available</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Boolean(homeTeamArticles?.items?.length) && Boolean(awayTeamArticles?.items?.length) && (
              <div className="match-overview__news-container">
                <h3>Latest news</h3>
                <div className="match-overview__news-list-container">
                  {homeTeamArticles?.items && homeTeamArticles.items.length > 0 && match.homeTeam?.fullName && (
                    <div className="match-overview__news-team-container">
                      <ArticleListUI
                        paginatedArticles={homeTeamArticles}
                        variant={ArticleVariant.TextSideBySide}
                        showDate={false}
                        columns={isMobile ? 1 : awayTeamArticles && awayTeamArticles?.items?.length > 0 ? 1 : 3}
                      />
                      <LinkButton
                        label={`More ${match.homeTeam.shortName} news`}
                        url={
                          isLeagueSite
                            ? `/news?tags=${match.homeTeam?.club?.slug}`
                            : `/news?tags=${match.homeTeam?.slug}`
                        }
                        openInNewTab={false}
                        variant={LinkVariant.Text}
                        size={LinkSize.sm}
                      />
                    </div>
                  )}
                  {awayTeamArticles?.items && awayTeamArticles.items.length > 0 && match.awayTeam?.fullName && (
                    <div className="match-overview__news-team-container">
                      <ArticleListUI
                        paginatedArticles={awayTeamArticles}
                        variant={ArticleVariant.TextSideBySide}
                        showDate={false}
                        columns={isMobile ? 1 : homeTeamArticles && homeTeamArticles?.items?.length > 0 ? 1 : 3}
                      />
                      <LinkButton
                        label={`More ${match.awayTeam.shortName} news`}
                        url={
                          isLeagueSite
                            ? `/news?tags=${match.awayTeam?.club?.slug}`
                            : `/news?tags=${match.awayTeam?.slug}`
                        }
                        openInNewTab={false}
                        variant={LinkVariant.Text}
                        size={LinkSize.sm}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="match-overview__newsletter-container">
          <NewsletterSubscribe title={'Get the latest news straight to your inbox'} />
        </div>

        <div className="match-overview__sponsors-container">
          <h3>Our Trusted Partners</h3>
          <SponsorsGrid
            className="matches-list__sponsors-grid"
            showAllSponsors
            showImage
            columns={isMobile ? 3 : 5}
            showName={false}
            showDescription={false}
          />
        </div>
      </div>
    </div>
  );
}
